import { ReactNode } from "react";

import { COPY } from "app/constants";
import { BsMetaUtil, getPandoraErrorMessage, isValidLeverage, LoopExpanded, BestQuote } from "@bridgesplit/abf-react";
import { bpsToPercent, formatNum, lamportsToUiAmount, minInputInToken } from "@bridgesplit/utils";
import { SwapRouteMinInputUsd } from "app/utils/domain/swap-routes";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { OracleQuote } from "@bridgesplit/abf-sdk";
import { SwapInstructionsResponse } from "@loopscale/pandora-types";

import { WindForm, LoopMode } from "./types";

const MIN_COLLATERAL_USD = 1;
const ERR_PRICE_IMPACT_PCT = 0.02;

interface LoopBorrowCapDetails {
    remainingGlobalCapacity: number;
    exceedsGlobalCap: (amount: number | undefined) => boolean;
    exceedsPerLoanCap: (amount: number | undefined) => boolean;
    borrowCap: {
        perLoan: number;
    } | null;
    percentOfCap: number;
    warningMessage: string | null;
}

interface GetCtaErrorParams {
    loopMode: LoopMode;
    form: WindForm;
    borrowQuoteFetching: boolean;
    isQuoteFetching: boolean;
    loopExpanded: LoopExpanded | undefined;
    collateralAmount: number | undefined;
    bestBorrowQuote: BestQuote | undefined;
    quote: any;
    externalQuote: SwapInstructionsResponse | undefined;
}

interface GetAlertErrorParams extends GetCtaErrorParams {
    isPermissionedToken: boolean;
    user: string | undefined;
    borrowCapDetails: LoopBorrowCapDetails | undefined | null;
    insufficientBalance: boolean;
    windDisabledWithMessage: string | undefined;
    externalQuoteError: FetchBaseQueryError | SerializedError | undefined;
    collateralOracle: OracleQuote | undefined;
    collateralInputAmount: number | undefined;
    leveragedCollateralAmountToDeposit: number | undefined;
    routeMinDeposit: SwapRouteMinInputUsd | undefined;
    inputTokenSymbol: string | undefined;
    inputTokenUsdPrice: number | undefined;
}

export function getCtaError(params: GetCtaErrorParams): string | undefined {
    const { loopMode, form, borrowQuoteFetching, isQuoteFetching, loopExpanded, collateralAmount } = params;

    // Common errors first
    // TODO NEED TO ADD RESTRCITIONS
    // Manual mode validation
    if (loopMode === LoopMode.Manual) {
        if (borrowQuoteFetching) return "Loading offers...";
        if (!form.collateralAmount) return "Enter collateral amount";
        if (!form.principalAmount) return "Enter borrow amount";
        if (!form.strategyDuration) return "Select duration";
        return undefined;
    }

    // auto mode validation
    if (!isValidLeverage(loopExpanded?.loopVault?.maxLeverage)) return "No leverage available";
    if (!collateralAmount) return "Enter an amount";
    if (!form.inputAmount) return "Enter an amount";
    if (!isValidLeverage(form.multiplier)) return `Enter ${COPY.LEVERAGE_TERM.toLowerCase()}`;
    if (isQuoteFetching) return "Fetching";

    return undefined;
}

export function getAlertError(params: GetAlertErrorParams): ReactNode | null | undefined {
    const {
        loopMode,
        form,
        isQuoteFetching,
        user,
        borrowCapDetails,
        loopExpanded,
        windDisabledWithMessage,
        collateralAmount,
        bestBorrowQuote,
        quote,
        externalQuote,
        externalQuoteError,
        collateralOracle,
        collateralInputAmount,
        leveragedCollateralAmountToDeposit,
        routeMinDeposit,
        inputTokenSymbol,
        inputTokenUsdPrice,
        insufficientBalance
    } = params;

    // Common errors
    if (isQuoteFetching) return undefined;
    if (!user) return "No wallet connected";
    if (borrowCapDetails?.remainingGlobalCapacity === 0)
        return `${BsMetaUtil.getSymbol(loopExpanded?.principalToken)} borrow cap exceeded`;

    // Manual mode errors
    if (loopMode === LoopMode.Manual) {
        if (!form.principalAmount || !form.strategyDuration) return undefined;
        if (!bestBorrowQuote) return "No matching offers";
        if (!form.collateralAmount) return undefined;
        if (insufficientBalance) return "Insufficient collateral balance";

        if (borrowCapDetails && form.principalAmount) {
            const { exceedsGlobalCap, exceedsPerLoanCap, borrowCap } = borrowCapDetails;
            if (exceedsPerLoanCap(form.principalAmount))
                return `Exceeds ${BsMetaUtil.formatAmount(
                    loopExpanded?.principalToken,
                    lamportsToUiAmount(borrowCap?.perLoan, loopExpanded?.principalToken?.decimals)
                )} per loan`;
            if (exceedsGlobalCap(form.principalAmount)) return "Exceeds borrow cap";
        }
        return null;
    }

    // auto mode errors
    if (windDisabledWithMessage) return windDisabledWithMessage;

    if (!isQuoteFetching && collateralAmount && form.multiplier) {
        if (quote === null) return "No matching liquidity. Please try lower deposit amount or leverage";
        if (externalQuoteError && user) {
            const errorMessage = getPandoraErrorMessage(
                externalQuoteError,
                "Unable to find a swap route through our integration partners."
            );
            return errorMessage;
        }
        if (!externalQuote && user) return "Error building swap quote, please refresh and try again";
    }

    // Min collateral USD check (auto only)
    const collateralUsd = collateralOracle?.getUsdAmount(collateralInputAmount);
    if (collateralUsd && collateralUsd < MIN_COLLATERAL_USD) return `Positions must be at least $${MIN_COLLATERAL_USD}`;

    // Route minimum is denominated in its step-input token; both sides compare in oracle USD.
    if (routeMinDeposit && collateralUsd !== undefined && collateralUsd < routeMinDeposit.minUsd) {
        // Say it in the deposit token when we can price it.
        if (inputTokenSymbol && inputTokenUsdPrice) {
            const minInputAmount = minInputInToken(routeMinDeposit.minUsd, inputTokenUsdPrice);
            return `Minimum deposit is ${formatNum(minInputAmount)} ${inputTokenSymbol}`;
        }
        return `Minimum deposit is ${formatNum(routeMinDeposit.amountUi)} ${routeMinDeposit.symbol}`;
    }

    if (
        leveragedCollateralAmountToDeposit &&
        loopExpanded?.maxCollateralAmount &&
        leveragedCollateralAmountToDeposit > loopExpanded?.maxCollateralAmount
    )
        return `Exceeds max ${BsMetaUtil.getSymbol(loopExpanded?.collateralToken)} amount`;

    if (borrowCapDetails && quote?.principalAmount) {
        const { exceedsGlobalCap, exceedsPerLoanCap } = borrowCapDetails;
        if (exceedsPerLoanCap(quote.principalAmount)) return "Exceeds borrow limit";
        if (exceedsGlobalCap(quote.principalAmount)) return "Exceeds borrow cap";
    }

    const priceImpactPct = externalQuote?.quote.priceImpactDecimals
        ? bpsToPercent(externalQuote.quote.priceImpactDecimals)
        : 0;
    if (priceImpactPct > ERR_PRICE_IMPACT_PCT) return `Exceeds price impact limit`;
    if (insufficientBalance) return "Insufficient balance";

    return undefined;
}
